/* eslint-disable @typescript-eslint/naming-convention */
import {
    AddressInfo,
    AddressEntity,
    BOOLEAN_AS_STRING,
    ReviewedCartApiResponse,
    AddressApiResponse,
    Location,
    AddressNudgeConfig,
} from "@swiggy-private/cart-types";
import { ADDRESS_CATEGORIES } from "@swiggy-private/core-types";
import { getBridgeSdk, GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import { format, isVersionCodeEligible } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { GetGPSLocationResponsePayload } from "@swiggy-private/native-bridge";

export const hasCartAddressSelectorXPDisabled = (xpData?: ReviewedCartApiResponse["XPData"]) =>
    xpData && xpData["disable_cart_address_selector"] === BOOLEAN_AS_STRING.TRUE;

// checkout mapping ref: https://github.com/swiggy-private/checkout-services-apis/blob/218f26f83f5e7499cd8f8cd690f7f53018b2af63/src/main/java/com/swiggy/api/services/address/helper/CasHelper.java#L81
export const transformCasAddressEntityToAddressInfo = (address: AddressEntity, rank: number): AddressInfo => ({
    id: address?.id ?? "",
    flat_no: address?.actual_address?.address_line,
    address_line2: address?.actual_address?.address_line_2,
    address_line_2: address?.actual_address?.address_line_2,
    landmark: address?.actual_address?.landmark,
    address: address?.actual_address?.formatted_address,
    annotation: address?.actual_address?.tags && address.actual_address.tags[0] ? address.actual_address.tags[0] : "",
    name: address?.actual_address?.contact_name,
    lat: address?.actual_address?.location?.lat?.toString(),
    lng: address?.actual_address?.location?.lng?.toString(),
    rank,

    /*
    Added user_intent.address_category as part of the address revamp.
    New addresses now include two fields:
     1. address_category → the system-assigned address type (exists for old addresses as well)
     2. user_intent.address_category → the category originally intended by the user 
    */
    addressCategory: address?.actual_address?.address_category?.[0] ?? ADDRESS_CATEGORIES.ADDRESS_CATEGORY_OTHER,
    userIntentAddressCategory: address?.actual_address?.user_intent?.address_category,
});

export const getAddressListFromAddressResponse = (data: AddressApiResponse): AddressInfo[] => {
    if (isEmpty(data) || isEmpty(data.addresses)) {
        return [];
    }
    return data.addresses.map((addressEntity, index) =>
        transformCasAddressEntityToAddressInfo(addressEntity, index + 1),
    );
};

/**
 * Sorts the given address list based on the order in a sorted list obtained from the bridge SDK.
 * If the sorted list is empty, not enabled for sorting, or has no addresses, the original list is returned.
 * Currently, the sorting is done in O(n^2) time complexity but can be improved to O(n) if the list is too big.
 *
 * @param {AddressInfo[]} addressList - The list of addresses to be sorted.
 * @returns {Promise<AddressInfo[]>} - A promise that resolves to the sorted list of addresses.
 * @throws {Error} - If an error occurs while getting the sorted list from the bridge SDK.
 */
export async function sortCartAddressList(addressList: AddressInfo[]): Promise<AddressInfo[]> {
    try {
        const allSortedList = await getBridgeSdk().getSortedAddressList();

        if (isEmpty(allSortedList) || !allSortedList.enableCxAppAddressSorting || isEmpty(allSortedList.addresses)) {
            return addressList;
        }

        const sortedAddressList: AddressInfo[] = [];

        allSortedList?.addresses?.forEach((sortedAddress) => {
            const address = addressList.find((address) => address.id === sortedAddress.addressId);

            if (!isEmpty(address)) {
                sortedAddressList.push(address);
            }
        });

        // [edge case/extra check] If list returned from apps is empty but not for CAS then return original list.
        return isEmpty(sortedAddressList) && !isEmpty(addressList) ? addressList : sortedAddressList;
    } catch (err) {
        return addressList;
    }
}

export const getCurrentLocation = async (): Promise<GetGPSLocationResponsePayload | undefined> => {
    try {
        const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode) || "";
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        const MIN_ANDROID_VERSION = 1385;
        const MIN_IOS_VERSION = 620;
        if (isVersionCodeEligible(MIN_ANDROID_VERSION, MIN_IOS_VERSION, versionCode, platform)) {
            return await getBridgeSdk().getGpsLocation();
        }
        return;
    } catch (error) {
        return;
    }
};

export const getHaversineDistance = (position1?: Location, position2?: Location): number | undefined => {
    if (!(position1 && position2)) {
        return;
    }
    const toRad = (value: number): number => {
        return (value * Math.PI) / 180;
    };

    const R = 6371; // km
    const x1 = Number(position2?.lat) - Number(position1?.lat);
    const dLat = toRad(x1);
    const x2 = Number(position2?.lng) - Number(position1?.lng);
    const dLon = toRad(x2);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(parseFloat(String(position1?.lat)))) *
            Math.cos(toRad(parseFloat(String(position2?.lat)))) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return parseFloat((R * c * 1000).toFixed(2));
};

export const getDistanceLabel = (distance: number): string => {
    //takes distance and adds distance metric m/km depending upon its value
    return distance < 1000 ? `${Math.trunc(distance)} m` : `${(distance / 1000).toFixed(1)} km`;
};

export const getAddressNudgeTooltipData = (
    addressNudgeConfig: AddressNudgeConfig,
): {
    addressNudgeToolTipDuration: number;
    addressNudgeToolTipText: string | undefined;
} => {
    const {
        cart_address_nudge_popup_duration: addressNudgeToolTipDuration = 5000,
        cart_address_nudge_message: message,
        haversineDistance,
    } = addressNudgeConfig;

    if (!(haversineDistance && message)) {
        return {
            addressNudgeToolTipDuration,
            addressNudgeToolTipText: undefined,
        };
    }

    const distanceLabel = getDistanceLabel(haversineDistance);

    return {
        addressNudgeToolTipDuration,
        addressNudgeToolTipText: format(message, distanceLabel),
    };
};
